import { FC } from "react";
import { useQuery } from '@tanstack/react-query';
import { useParams } from "react-router-dom";
import styled from "styled-components";

import Text from "@/components/Text";
import TrendCard from "@/components/Metrics/TrendCard";
import LinkCard from "@/components/Metrics/LinkCard";
import { api } from "@/utils/api";
import { TrendMetricCard, PerformanceGroup, Period } from "@/types/resources";
import useChannelsFilter from "@/hooks/useChannelsFilter";
import useDidUpdate from "@/hooks/useDidUpdate";

const SectionHeading = styled(Text)`
  margin: 0 0 12px;
  text-transform: uppercase;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-gap: 16px;
`;

type Metrics = {
  updated_at: string;
  cards: TrendMetricCard[];
};

interface Props {
  group: PerformanceGroup;
  period: Period;
}

const PerformanceIndexGroup: FC<Props> = ({ group, period }) => {
  const { teamId } = useParams();

  const [filteredChannelsIds, filteredChannelsKey] = useChannelsFilter();

  const { data, remove, refetch } = useQuery<Metrics>(
    ["performance", { period, teamId, group }],
    () =>
      api.get<never, Metrics>(`/v1/teams/${teamId}/performance/${group}`, {
        params: {
          period,
          ...(filteredChannelsIds?.length && {
            "filter[channel_id]": filteredChannelsIds.join(","),
          }),
        },
      })
  );

  useDidUpdate(() => {
    remove();
    refetch();
  }, [filteredChannelsKey]);

  return (
    <section>
      <SectionHeading as="h3" $color="gray" $type="smallBold">
        {group} Performance Indexes
      </SectionHeading>
      <Grid>
        {!!data && (
          <>
            {data?.cards.map((card, index) => (
              <TrendCard card={card} key={index} />
            ))}
            <LinkCard />
          </>
        )}
      </Grid>
    </section>
  );
};

export default PerformanceIndexGroup;
